import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { discountPercent, formatPara, para, type Rsd } from '../core/money.ts';
import { useStrings } from '../i18n/index.ts';
import { toastsStore } from '../state/toasts.ts';
import { useStore } from '../core/store.ts';
import { Icon, type IconName } from './icons.tsx';

/** Общие элементы интерфейса: кнопки, поля, карточки, заголовки секций, пустые состояния. */

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/** Пастель плитки по индексу: соседние плитки разные, у категории цвет всегда один. */
export function pastel(index: number): string {
  return `var(--pastel-${Math.abs(index) % 7})`;
}

/** Пастель по идентификатору: одна и та же между запусками. */
export function pastelFor(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) & 0xffff;
  return pastel(hash);
}

export function Spinner({ size = 20 }: { size?: number }) {
  return <span className="spinner" style={{ '--size': `${size}px` } as CSSProperties} role="progressbar" />;
}

export function Button(props: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  kind?: 'primary' | 'secondary' | 'danger';
  small?: boolean;
  disabled?: boolean;
  busy?: boolean;
  type?: 'button' | 'submit';
  className?: string;
}) {
  return (
    <button
      type={props.type ?? 'button'}
      className={cx('btn', props.kind && props.kind !== 'primary' && props.kind, props.small && 'small', props.className)}
      disabled={props.disabled || props.busy}
      onClick={props.onClick}
    >
      {props.busy ? <Spinner /> : props.children}
    </button>
  );
}

/** Главная кнопка закреплённой нижней панели: в карточке товара, корзине и оформлении. */
export function BarButton(props: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  disabled?: boolean;
  busy?: boolean;
  dark?: boolean;
}) {
  return (
    <button type="button" className={cx('bar-button', props.dark && 'is-dark')} disabled={props.disabled || props.busy} onClick={props.onClick}>
      {props.busy ? <Spinner /> : props.children}
    </button>
  );
}

/** Круглая кнопка на стекле: «назад», закрыть, действия в полосе навигации. */
export function GlassButton(props: { icon: IconName; label: string; onClick: () => void; tone?: 'danger' | 'accent' }) {
  return (
    <button
      type="button"
      className={cx('glass-button', props.tone === 'danger' && 'is-danger', props.tone === 'accent' && 'is-accent')}
      aria-label={props.label}
      onClick={props.onClick}
    >
      <Icon name={props.icon} size={20} weight={2.4} />
    </button>
  );
}

/** Круглая кнопка с иконкой на белой подложке и счётчиком. */
export function RoundIconButton(props: { icon: IconName; label: string; badge?: number; onClick: () => void; className?: string }) {
  return (
    <button type="button" className={cx('round-icon', props.className)} aria-label={props.label} onClick={props.onClick}>
      <Icon name={props.icon} size={18} />
      {props.badge !== undefined && props.badge > 0 && <span className="badge-count">{props.badge > 99 ? '99+' : props.badge}</span>}
    </button>
  );
}

export function Card(props: { children: ReactNode; flush?: boolean; className?: string; style?: CSSProperties }) {
  return (
    <div className={cx('card', props.flush && 'flush', props.className)} style={props.style}>
      {props.children}
    </div>
  );
}

/** Строка списка в карточке: иконка, название, значение и шеврон. */
export function ListRow(props: {
  icon?: IconName;
  title: ReactNode;
  subtitle?: ReactNode;
  value?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
  onClick?: (() => void) | undefined;
  tone?: 'danger';
}) {
  const body = (
    <>
      {props.icon && <Icon name={props.icon} className="lead-icon" size={22} />}
      <span className="grow">
        <span style={{ display: 'block', fontSize: 17, color: props.tone === 'danger' ? 'var(--danger)' : undefined }}>{props.title}</span>
        {props.subtitle && <span className="t-footnote t-muted" style={{ display: 'block' }}>{props.subtitle}</span>}
      </span>
      {props.value && <span className="value">{props.value}</span>}
      {props.trailing}
      {props.chevron && <Icon name="chevronRight" className="chevron" size={16} weight={2.6} />}
    </>
  );
  return props.onClick ? (
    <button type="button" className="list-row" onClick={props.onClick}>
      {body}
    </button>
  ) : (
    <div className="list-row">{body}</div>
  );
}

/** Заголовок секции главной: крупный текст слева, «Смотреть всё» справа. */
export function SectionHeader(props: { title: string; subtitle?: string | null; actionTitle?: string; onAction?: () => void }) {
  return (
    <div className="section-header">
      <div>
        <h2>{props.title}</h2>
        {props.subtitle && <p>{props.subtitle}</p>}
      </div>
      {props.actionTitle && props.onAction && (
        <button type="button" className="pill-action" onClick={props.onAction}>
          {props.actionTitle}
        </button>
      )}
    </div>
  );
}

/** Чип: фильтр, категория в ленте, подкатегория на экране категории. */
export function Chip(props: { title: string; icon?: IconName; selected?: boolean; onClick: () => void; style?: CSSProperties }) {
  return (
    <button type="button" className={cx('chip', props.selected && 'is-selected')} onClick={props.onClick} style={props.style}>
      {props.icon && <Icon name={props.icon} size={15} weight={2.4} />}
      {props.title}
    </button>
  );
}

/** Счётчик количества: минус (корзина при единице), число с фасовкой, плюс. */
export function QuantityStepper(props: {
  quantity: number;
  canIncrease: boolean;
  compact?: boolean;
  flat?: boolean;
  /** Вторая часть после числа (`× 120 g`, `kg`), см. `packSizeDetail`. */
  detail?: string | null;
  onDecrease: () => void;
  onIncrease: () => void;
}) {
  const strings = useStrings();
  const iconSize = props.compact ? 15 : 16;
  return (
    <div
      className={cx('stepper', props.compact && 'compact', props.flat && 'flat')}
      role="group"
      aria-label={strings.a11yQuantity}
      onClick={(event) => event.stopPropagation()}
    >
      <button type="button" aria-label={props.quantity === 1 ? strings.a11yRemoveFromCart : strings.a11yDecrease} onClick={props.onDecrease}>
        <Icon name={props.quantity === 1 ? 'trash' : 'minus'} size={iconSize} weight={2.6} />
      </button>
      <span className="count">
        <b>{props.quantity}</b>
        {props.detail && <span>{props.detail}</span>}
      </span>
      <button type="button" aria-label={strings.a11yIncrease} disabled={!props.canIncrease} onClick={props.onIncrease}>
        <Icon name="plus" size={iconSize} weight={2.6} />
      </button>
    </div>
  );
}

/** Круглый «плюс» в углу фото товара. */
export function AddButton(props: { enabled: boolean; size?: number; onClick: () => void }) {
  const strings = useStrings();
  const size = props.size ?? 40;
  return (
    <button
      type="button"
      className="add-button"
      style={{ '--size': `${size}px` } as CSSProperties}
      aria-label={strings.a11yAddToCart}
      disabled={!props.enabled}
      onClick={(event) => {
        event.stopPropagation();
        props.onClick();
      }}
    >
      <Icon name="plus" size={size * 0.48} weight={2.8} />
    </button>
  );
}

/** Бейдж скидки в процентах: красная пилюля на фото. */
export function SaleBadge({ price, compareAt }: { price: Rsd; compareAt: Rsd }) {
  const percent = discountPercent(para(price), para(compareAt));
  return percent === null ? null : <span className="sale-badge">−{percent}%</span>;
}

/** Цена: со скидкой красная, старая зачёркнута серым. */
export function PriceLabel({ price, compareAt, size = 17 }: { price: Rsd; compareAt?: Rsd | null | undefined; size?: number }) {
  const hasOld = compareAt != null && para(compareAt) > para(price);
  return (
    <span className={cx('price', compareAt != null && 'is-sale')} style={{ '--price-size': `${size}px` } as CSSProperties}>
      <b>{formatPara(para(price))}</b>
      {hasOld && <s>{formatPara(para(compareAt))}</s>}
    </span>
  );
}

export function EmptyState(props: { icon?: IconName; image?: string; title: string; hint: string; actionTitle?: string; onAction?: () => void }) {
  return (
    <div className="empty-state">
      {props.image ? (
        <img src={props.image} alt="" width={150} height={150} />
      ) : (
        <span className="icon">
          <Icon name={props.icon ?? 'basket'} size={44} weight={1.5} />
        </span>
      )}
      <h3>{props.title}</h3>
      <p>{props.hint}</p>
      {props.actionTitle && props.onAction && (
        <Button kind="secondary" onClick={props.onAction}>
          {props.actionTitle}
        </Button>
      )}
    </div>
  );
}

/** Сообщение об ошибке над содержимым: текст по коду ответа, рядом повтор. */
/** Текст с markdown-ссылками `[текст](url)`: каждая становится ссылкой в новой вкладке. */
export function LinkedText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    parts.push(text.slice(last, m.index));
    parts.push(
      <a key={m.index} href={m[2]} target="_blank" rel="noreferrer">
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

export function ErrorBanner({ text, onRetry }: { text: string; onRetry?: (() => void) | undefined }) {
  const strings = useStrings();
  return (
    <div className="error-banner" role="alert">
      <Icon name="warning" size={18} />
      <span>{text}</span>
      {onRetry && (
        <button type="button" className="text-button" onClick={onRetry}>
          {strings.retry}
        </button>
      )}
    </div>
  );
}

/** Полоса прогресса до бесплатной доставки; доля от нуля до единицы. */
export function ProgressBar({ progress, height = 4 }: { progress: number; height?: number }) {
  return (
    <div className="progress" style={{ '--progress-h': `${height}px` } as CSSProperties}>
      <i style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }} />
    </div>
  );
}

export function Skeleton(props: { width?: number | string; height?: number | string; radius?: number; style?: CSSProperties }) {
  return (
    <div
      className="skeleton"
      style={{ width: props.width, height: props.height, ...(props.radius !== undefined ? { borderRadius: props.radius } : {}), ...props.style }}
    />
  );
}

/** Крупный заголовок экрана первым элементом ленты. */
export function ScreenTitle({ text, subtitle }: { text: string; subtitle?: string | null | undefined }) {
  return (
    <div className="screen-title">
      <h1>{text}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

/** Поле ввода в общем стиле; `muted` — поле внутри белой карточки. */
export function FieldBox(props: { label?: string; invalid?: boolean; muted?: boolean; children: ReactNode }) {
  return (
    <label className={cx('field', props.invalid && 'invalid', props.muted && 'muted')}>
      {props.label && <span>{props.label}</span>}
      <span className="box">{props.children}</span>
    </label>
  );
}

export function Toggle(props: { checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      className="toggle"
      aria-checked={props.checked}
      aria-label={props.label}
      disabled={props.disabled}
      onClick={() => props.onChange(!props.checked)}
    />
  );
}

/** Диалог подтверждения с одним действием и отменой. */
export function ConfirmDialog(props: {
  title: string;
  text?: string;
  confirmTitle: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const strings = useStrings();
  return (
    <div className="dialog-layer" onClick={props.onCancel}>
      <div className="dialog" role="alertdialog" aria-label={props.title} onClick={(event) => event.stopPropagation()}>
        <h3>{props.title}</h3>
        {props.text && <p>{props.text}</p>}
        <div className="dialog-actions">
          <Button small kind={props.destructive ? 'danger' : 'primary'} busy={props.busy ?? false} onClick={props.onConfirm}>
            {props.confirmTitle}
          </Button>
          <Button small kind="secondary" onClick={props.onCancel}>
            {strings.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Слой подсказок поверх экрана и листов. */
export function ToastLayer() {
  const items = useStore(toastsStore);
  if (items.length === 0) return null;
  return (
    <div className="toasts" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className="toast">
          {item.text}
        </div>
      ))}
    </div>
  );
}

/** Картинка каталога: появляется проявлением, без ссылки или при ошибке — заглушка. */
export function RemoteImage({ src, cover, eager, alt = '' }: { src: string | null | undefined; cover?: boolean; eager?: boolean; alt?: string }) {
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  useEffect(() => setState('loading'), [src]);

  if (!src || state === 'failed') {
    return (
      <span className="image-placeholder">
        <Icon name="basket" size={28} weight={1.5} />
      </span>
    );
  }
  return (
    <img
      className={cx('remote-image', cover && 'cover', state === 'loaded' && 'is-loaded')}
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
      ref={(node) => {
        // Картинка из кеша браузера готова до подписки на `load`.
        if (node?.complete && node.naturalWidth > 0) setState('loaded');
      }}
      onLoad={() => setState('loaded')}
      onError={() => setState('failed')}
    />
  );
}
